import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Filter, Calendar, Info } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useNavigate } from "react-router-dom";
import { useExperienceStore } from "@/lib/stores/experience-store";
import { ExperienceCard } from "@/components/experiences/ExperienceCard";
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

export function Experiences() {
  const navigate = useNavigate();
  const { experiences, loading, error, loadExperiences } = useExperienceStore();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [showFilters, setShowFilters] = React.useState(false);

  useEffect(() => {
    loadExperiences();
  }, [loadExperiences]);

  const filteredExperiences = experiences.filter(
    (exp) =>
      !searchQuery ||
      exp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exp.description.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="min-h-screen bg-earth-900">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-earth-900/95 backdrop-blur-sm pt-20 pb-4 border-b border-earth-800">
        <div className="container mx-auto px-4">
          {/* Search and Filter Bar */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-sand-400" />
              <input
                type="text"
                placeholder="Search experiences..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-none bg-earth-800 border border-earth-700 text-sand-100 focus:outline-none focus:ring-1 focus:ring-sand-400"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="md:w-auto w-full justify-center"
            >
              <Filter className="h-5 w-5 mr-2" />
              Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4">
        {/* Add padding to account for fixed header */}
        <div className="pt-48 pb-12">
          {/* Upcoming Experiences */}
          <div className="mb-16">
            <h2 className="text-2xl font-display text-sand-100 mb-8">Upcoming Experiences</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              
              {/* Goat Festival - New tile with external link */}
              <motion.div
                className="relative cursor-pointer group"
                whileHover={{ y: -5 }}
                onClick={() => window.open("http://goatcommunity.pt", "_blank")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Goat%20Festival/__post.png"
                    alt="Goat Festival"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Goat Festival
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">July 2-7</p>
                    </div>
                  </div>
                </div>
              </motion.div>                          

                
              {/* Xamanic Journey Tile */}
              <motion.div
                className="relative cursor-pointer group"
                whileHover={{ y: -5 }}
                onClick={() => navigate("/experiences/xamanic")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Xamanic/Xamanic%20Flyer%20Reformat.png"
                    alt="Xamanic Journey - Basilicata"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />                  
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Xamanic Journey - Basilicata
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">July 25-27</p>
                    </div>
                  </div>
                </div>
              </motion.div>           

              {/* Astral Plans */}
              <motion.div
                className="relative cursor-pointer group"
                whileHover={{ y: -5 }}
                onClick={() => window.open("https://www.instagram.com/astral_plans/", "_blank")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Astral/WhatsApp%20Image%202025-06-05%20at%2015.07.11_d294120c.jpg"
                    alt="Astral Plans"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Astral Plans
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">Aug 12-18</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            
            </div>
          </div>

          {/* Past Experiences Section */}
          <div className="mb-16">
            <h2 className="text-2xl font-display text-sand-100 mb-8">Past Experiences</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Lucido April 12th - Now in Past Experiences */}
              <motion.div
                className="relative cursor-pointer group opacity-80 hover:opacity-100 transition-opacity"
                whileHover={{ y: -5 }}
                onClick={() => navigate("/lucido-april-12")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Lucido/botanical-garden-preview.jpg"
                    alt="Lucido"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute top-2 right-2 bg-earth-900/80 text-sand-400 text-xs px-2 py-1 rounded">
                    Completed
                  </div>
                  {/* Location Tag */}
                  <div className="absolute top-2 left-2 bg-earth-900/80 backdrop-blur-sm text-sand-300 text-xs px-2 py-1 rounded">
                    Miami
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Lúcido - Miami
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">April 12, 2025</p>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Ayuni - Moved to Past Experiences */}
              <motion.div
                className="relative cursor-pointer group opacity-80 hover:opacity-100 transition-opacity"
                whileHover={{ y: -5 }}
                onClick={() => navigate("/experiences/ayuni")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Ayuni/WhatsApp%20Image%202025-04-17%20at%2017.55.45_a7e318e9.jpg"
                    alt="Ayuni"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute top-2 right-2 bg-earth-900/80 text-sand-400 text-xs px-2 py-1 rounded">
                    Completed
                  </div>
                  {/* Location Tag */}
                  <div className="absolute top-2 left-2 bg-earth-900/80 backdrop-blur-sm text-sand-300 text-xs px-2 py-1 rounded">
                    Miami
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Ayuni Dining Experience
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">April 27</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* ArtHouse */}
              <motion.div
                className="relative cursor-pointer group opacity-80 hover:opacity-100 transition-opacity"
                whileHover={{ y: -5 }}                               
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Sedef%20Dinner%20Party/Background%20image.jpg"
                    alt="ArtHouse by Sedef Gali"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute top-2 right-2 bg-earth-900/80 text-sand-400 text-xs px-2 py-1 rounded">
                    Completed
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      ArtHouse Spring Dinner Party
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">May 22</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Tithorea */}
              <motion.div
                className="relative cursor-pointer group opacity-80 hover:opacity-100 transition-opacity"
                whileHover={{ y: -5 }}
                onClick={() => window.open("https://www.tithorea.com/", "_blank")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Medaina%20-%20preview.jpg"
                    alt="Tithorea - Medaina"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute top-2 right-2 bg-earth-900/80 text-sand-400 text-xs px-2 py-1 rounded">
                    Completed
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Tithorea - Petra and Wadi Rum
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">May 21-26</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Aegean Odyssey - Voyage 1 */}
              <motion.div
                className="relative cursor-pointer group opacity-80 hover:opacity-100 transition-opacity"
                whileHover={{ y: -5 }}
                onClick={() => navigate("/experiences/aegean")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/Tile%20Flyer.png"
                    alt="Aegean Odyssey"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute top-2 right-2 bg-earth-900/80 text-sand-400 text-xs px-2 py-1 rounded">
                    Completed
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Aegean Odyssey - Voyage 1
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">May 26 - June 2</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/*Per Anhalter */}
              <motion.div
                className="relative cursor-pointer group opacity-80 hover:opacity-100 transition-opacity"
                whileHover={{ y: -5 }}
                onClick={() => window.open("https://us9.campaign-archive.com/?u=b1463de2a30c7eadf57504d2c&id=296ac0a06d", "_blank")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/Screenshot%202025-05-07%20154915.png"
                    alt="Per Anhalter"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute top-2 right-2 bg-earth-900/80 text-sand-400 text-xs px-2 py-1 rounded">
                    Completed
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Per Anhalter
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">May 27 - June 2</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/*Nomada */}
              <motion.div
                className="relative cursor-pointer group opacity-80 hover:opacity-100 transition-opacity"
                whileHover={{ y: -5 }}
                onClick={() => window.open("https://www.nomadawellness.com/jointhejourney-atlasmountains", "_blank")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Nomada/Poster/WhatsApp%20Image%202025-05-08%20at%2022.35.41_e6a4e008.jpg"
                    alt="Nomada"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute top-2 right-2 bg-earth-900/80 text-sand-400 text-xs px-2 py-1 rounded">
                    Completed
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Nomada - Atlas Mountains
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">May 27 - June 1</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Azores Tile */}
              <motion.div
                className="relative cursor-pointer group opacity-80 hover:opacity-100 transition-opacity"
                whileHover={{ y: -5 }}
                onClick={() => navigate("/experiences/azores")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Azores/Azores-preview.png"
                    alt="Discover the Azores"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute top-2 right-2 bg-earth-900/80 text-sand-400 text-xs px-2 py-1 rounded">
                    Completed
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Azores: Pico Island
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">May 28 - June 1</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Sacbe - New tile with external link */}
              <motion.div
                className="relative cursor-pointer group opacity-80 hover:opacity-100 transition-opacity"
                whileHover={{ y: -5 }}
                onClick={() => window.open("https://bit.ly/sacbe-ibiza", "_blank")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Sacbe/SOLSTICE_web.png"
                    alt="Sacbe"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute top-2 right-2 bg-earth-900/80 text-sand-400 text-xs px-2 py-1 rounded">
                    Completed
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Sacbe Solstice Re.Union - Ibiza
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">June 19-26</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              
              {/* Reconnect IV */}
              <motion.div
                className="relative cursor-pointer group opacity-80 hover:opacity-100 transition-opacity"
                whileHover={{ y: -5 }}
                onClick={() => window.open("https://shotgun.live/events/reconnect-iv?uhandle=brandolinog", "_blank")}
              >
                <div className="relative bg-earth-800/50 rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://rendezvouswebsite.b-cdn.net/Reconnect/WhatsApp%20Image%202025-06-05%20at%2018.58.49_626f238c.jpg"
                    alt="Reconnect IV"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/20 to-transparent" />
                  <div className="absolute top-2 right-2 bg-earth-900/80 text-sand-400 text-xs px-2 py-1 rounded">
                    Completed
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <h3 className="text-2xl font-display text-sand-100 mb-2">
                      Reconnect IV
                    </h3>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-4 w-4" />
                      <p className="text-sand-300">June 26-29</p>
                    </div>
                  </div>
                </div>
              </motion.div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}